import {
  Badge,
  Button,
  Checkbox,
  confirm,
  createSortableColumn,
  DataTable,
  Label,
  type TanstackReactTable,
} from "@devhop/ui";
import { CheckIcon, EditIcon, EyeIcon, TrashIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { PrivateLessonTemplateDTO } from "@/modules/private-lessons/domain/contracts";
import { multiMemberPackageLabel } from "@/modules/private-lessons/domain/lib/package-kind";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type PrivateLessonTemplateRow = PrivateLessonTemplateDTO & {
  packagesCount: number;
};

type PrivateLessonTemplatesTableProps = {
  data: PrivateLessonTemplateRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (template: PrivateLessonTemplateRow) => void;
  onEdit: (template: PrivateLessonTemplateRow) => void;
  onDelete: (id: string) => void;
  onBulkDelete?: (ids: string[]) => void;
  onBulkToggleStatus?: (
    ids: string[],
    status: "active" | "inactive",
  ) => void;
};

export function PrivateLessonTemplatesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onBulkToggleStatus,
}: PrivateLessonTemplatesTableProps) {
  const [columnSelection, setColumnSelection] = useState<string[]>([]);

  // Clear selection when data changes (pagination, filter, etc.)
  useEffect(() => {
    setColumnSelection([]);
  }, [data, offset]);

  const selectedCount = columnSelection.length;
  const allSelected = data.length > 0 && selectedCount === data.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setColumnSelection(data.map((row) => row.id));
    } else {
      setColumnSelection([]);
    }
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedCount === 0) return;

    const ok = await confirm({
      title: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວຫຼາຍລາຍການ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດຮຽນສ່ວນຕົວ ${selectedCount} ລາຍການ?`,
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (ok) {
      onBulkDelete(columnSelection);
      setColumnSelection([]);
    }
  };

  const handleBulkToggleStatus = async (status: "active" | "inactive") => {
    if (!onBulkToggleStatus || selectedCount === 0) return;

    const statusLabel =
      status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    const ok = await confirm({
      title: `ປ່ຽນສະຖານະ template ແພັກເກດຮຽນສ່ວນຕົວ ${selectedCount} ລາຍການ`,
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${statusLabel} template ແພັກເກດຮຽນສ່ວນຕົວ ${selectedCount} ລາຍການ?`,
      actionText: statusLabel,
    });

    if (ok) {
      onBulkToggleStatus(columnSelection, status);
      setColumnSelection([]);
    }
  };

  const columns: TanstackReactTable.ColumnDef<PrivateLessonTemplateRow>[] = [
    {
      enableSorting: false,
      accessorKey: "id",
      header: () => (
        <Checkbox
          checked={allSelected}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
          className="hidden md:block"
        />
      ),
      size: 50,
      cell: ({ row }) => (
        <Label className="flex items-center gap-2">
          <Checkbox
            checked={columnSelection.includes(row.original.id)}
            onCheckedChange={(checked) => {
              if (checked) {
                setColumnSelection([...columnSelection, row.original.id]);
              } else {
                setColumnSelection(
                  columnSelection.filter((id) => id !== row.original.id),
                );
              }
            }}
            aria-label={`Select ${row.original.name}`}
          />
          <span className="hidden font-medium text-sm max-md:block">ເລືອກ</span>
        </Label>
      ),
    },
    createSortableColumn("name", "ຊື່ແພັກເກດ"),
    {
      header: "ປະເພດ",
      accessorKey: "packageKind",
      cell: ({ row }) => {
        const label = multiMemberPackageLabel(row.original.packageKind);
        return (
          <Badge variant={label ? "outline" : "secondary"}>
            {label ?? "ສ່ວນຕົວ"}
          </Badge>
        );
      },
    },
    {
      header: "ສະຖານທີ່",
      accessorKey: "defaultLocationKind",
      cell: ({ row }) => {
        const isOffSite = row.original.defaultLocationKind === "off_site";
        return (
          <Badge variant={isOffSite ? "outline" : "secondary"}>
            {isOffSite ? "ນອກສະຖານທີ່" : "ສະຕູດິໂອ"}
          </Badge>
        );
      },
    },
    {
      header: "ຊົ່ວໂມງ / ຂະໜາດກຸ່ມ",
      accessorKey: "totalHours",
      cell: ({ row }) => {
        const t = row.original;
        if (t.packageKind === "group") {
          return (
            <div className="text-sm">
              <div className="text-right">{t.totalHours} ຊມ.</div>
              {t.groupSizeMin != null && t.groupSizeMax != null && (
                <div className="text-right text-muted-foreground text-xs">
                  {t.groupSizeMin}–{t.groupSizeMax} ຄົນ
                </div>
              )}
            </div>
          );
        }
        if (t.packageKind === "private_shared") {
          return (
            <div className="text-sm">
              <div className="text-right text-muted-foreground">ຊື້ເອງ</div>
              {t.groupSizeMin != null && t.groupSizeMax != null && (
                <div className="text-right text-muted-foreground text-xs">
                  tier {t.groupSizeMin}–{t.groupSizeMax}
                </div>
              )}
            </div>
          );
        }
        return <div className="text-right">1 ຫນ່ວຍ</div>;
      },
    },
    {
      header: "ຈຳນວນວັນ",
      accessorKey: "validDays",
      cell: ({ row }) => (
        <div className="text-right">{row.original.validDays}</div>
      ),
    },
    {
      header: "ລາຄາ (ກີບ)",
      accessorKey: "pricePerHour",
      cell: ({ row }) => {
        const t = row.original;
        if (t.packageKind === "group" && t.flatPrice != null) {
          return (
            <div className="text-right">
              {t.flatPrice.toLocaleString("lo-LA")}
              <span className="block text-muted-foreground text-xs">
                ຕໍ່ຄົນ
              </span>
            </div>
          );
        }
        if (t.packageKind === "private_shared" && t.tiers?.length) {
          const first = t.tiers[0]!;
          const last = t.tiers[t.tiers.length - 1]!;
          return (
            <div className="text-right text-sm">
              {first.pricePerHour.toLocaleString("lo-LA")}–
              {last.pricePerHour.toLocaleString("lo-LA")}
              <span className="block text-muted-foreground text-xs">₭/ຊມ.</span>
            </div>
          );
        }
        return (
          <div className="text-right">
            {t.pricePerHour.toLocaleString("lo-LA")}
            <span className="block text-muted-foreground text-xs">
              /ຊົ່ວໂມງ
            </span>
          </div>
        );
      },
    },
    {
      header: "ຈຳນວນແພັກເກດ",
      accessorKey: "packagesCount",
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.packagesCount > 0
            ? `${row.original.packagesCount} ແພັກເກດ`
            : "-"}
        </div>
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === "active" ? "default" : "secondary"}>
            {status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    {
      header: "ວັນທີສ້າງ",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: PrivateLessonTemplateRow } }) => {
        const { id } = row.original;
        const actions = [];

        actions.push({
          label: "ລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(row.original),
        });

        actions.push({
          label: "ແກ້ໄຂ",
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => onEdit(row.original),
        });

        actions.push({
          label: "ລຶບ",
          variant: "destructive" as const,
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວ",
              description:
                "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດຮຽນສ່ວນຕົວນີ້?",
              actionText: "ລຶບ",
              ActionProps: {
                variant: "destructive",
              },
            });
            if (ok) onDelete(id);
          },
        });

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <>
      {selectedCount > 0 && (
        <div className="flex flex-col gap-2 border-b bg-muted/50 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              ເລືອກ {selectedCount} ລາຍການ
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setColumnSelection([])}
            >
              <XIcon className="size-4" />
              <span className="hidden sm:inline">ຍົກເລີກ</span>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onBulkToggleStatus && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("active")}
                  className="flex-1 sm:flex-initial"
                >
                  <CheckIcon className="size-4" />
                  <span className="hidden sm:inline">ເປີດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ເປີດ</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("inactive")}
                  className="flex-1 sm:flex-initial"
                >
                  <XIcon className="size-4" />
                  <span className="hidden sm:inline">ປິດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ປິດ</span>
                </Button>
              </>
            )}
            {onBulkDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="flex-1 sm:flex-initial"
              >
                <TrashIcon className="size-4" />
                <span className="hidden sm:inline">ລຶບ ({selectedCount})</span>
                <span className="sm:hidden">ລຶບ</span>
              </Button>
            )}
          </div>
        </div>
      )}
      <DataTable<PrivateLessonTemplateRow, unknown>
        noDataMessage="ບໍ່ພົບ template ແພັກເກດຮຽນສ່ວນຕົວ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </>
  );
}
